// apps/regrade/components/PreferencesProvider.tsx
import React, { useMemo } from 'react';
import { setCookie, deleteCookie, getCookie } from 'cookies-next';
import { GlobalActionsProvider } from "@plasmicapp/react-web/lib/host";

const COOKIE_NAME = 'user-prefs';

// Helper to get current prefs from browser
const getCurrentPrefs = () => {
  if (typeof window === 'undefined') return {};
  const raw = getCookie(COOKIE_NAME);
  if (typeof raw === 'string') {
    try { return JSON.parse(decodeURIComponent(raw)); } catch { return {}; }
  }
  return {};
};

// Helper to broadcast change to PageWrapper
const notifyChange = (newPrefs: any) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('layout-preferences-updated', { detail: newPrefs }));
  }
};

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  
  const actions = useMemo(() => ({
    set: (key: string, value: any) => {
      // Handle Plasmic strings
      let finalValue = value;
      if (value === "true") finalValue = true;
      if (value === "false") finalValue = false;

      const current = getCurrentPrefs();
      const next = { ...current, [key]: finalValue };
      const jsonStr = JSON.stringify(next);

      console.log(`[Preferences] Set ${key}=${finalValue}`);
      const isHttps = (typeof window !== 'undefined' && window.location.protocol === 'https:')  ;
      setCookie(COOKIE_NAME, jsonStr, {
        maxAge: 60 * 60 * 24 * 365,
        path: '/',
        // IF HTTPS: Allow cross-site/iframe usage
        // IF HTTP: Fallback to Lax (iframe will fail, but app works directly)
        sameSite: isHttps ? 'none' : 'lax',
        secure: isHttps
      });
      notifyChange(next);
    },
    remove: (key: string) => {
      const current = getCurrentPrefs();
      const next = { ...current };
      delete next[key];
      const jsonStr = JSON.stringify(next);
      
      const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

      setCookie(COOKIE_NAME, jsonStr, {
        maxAge: 60 * 60 * 24 * 365,
        path: '/',
        sameSite: isHttps ? 'none' : 'lax',
        secure: isHttps
      });

      notifyChange(next);
    },
    clearAll: () => {
      deleteCookie(COOKIE_NAME, { path: '/' });
      notifyChange({});
    }
  }), []);

  return (
    <GlobalActionsProvider contextName="PreferencesProvider" actions={actions}>
      {children}
    </GlobalActionsProvider>
  );
}